import { type EnvironmentId } from "@cmmd-forge/contracts";
import { raceWithAbort, sleepWithAbort, throwIfAborted } from "./abortableWait";
import { type SavedEnvironmentRecord } from "./catalog";
import { type EnvironmentConnection } from "./connection";
import {
  CMMD_RUNTIME_BOOTSTRAP_MAX_ATTEMPTS,
  cmmdRuntimeBootstrapRetryDelayMs,
  isTransientCmmdRuntimeBootstrapError,
} from "./runtimeBootstrapPolicy";
import { useSavedEnvironmentRuntimeStore } from "./savedEnvironmentRuntimeStore";
import * as savedCloud from "./savedCloudEnvironmentLifecycle";
import { type SavedEnvironmentConnectionOptions } from "./savedEnvironmentConnection";
import {
  type SavedEnvironmentAcquisitionRegistryExpectation,
  type SavedEnvironmentConnectionRegistry,
} from "./savedEnvironmentConnectionRegistry";

export type CmmdRuntimeBootstrapDependencies = {
  readonly ensureSavedEnvironmentConnection: (
    record: SavedEnvironmentRecord,
    options?: SavedEnvironmentConnectionOptions,
  ) => Promise<EnvironmentConnection>;
  readonly getGeneration: (environmentId: EnvironmentId) => number;
  readonly registry: Pick<SavedEnvironmentConnectionRegistry, "isCurrent">;
  readonly removeConnection: (environmentId: EnvironmentId) => Promise<boolean>;
  readonly setRuntimeError: (environmentId: EnvironmentId, error: unknown) => void;
};

type ConnectedSavedEnvironmentBootstrapRetry = savedCloud.ConnectedSavedEnvironmentBootstrapRetry;
type CmmdRuntimeBootstrapOptions = {
  readonly bearerToken?: string;
  readonly signal?: AbortSignal;
  readonly onBootstrapRetry?: (progress: ConnectedSavedEnvironmentBootstrapRetry) => void;
  readonly registryExpectation?: SavedEnvironmentAcquisitionRegistryExpectation;
};

export async function ensureCmmdRuntimeSavedEnvironmentConnection(
  record: SavedEnvironmentRecord,
  options: CmmdRuntimeBootstrapOptions | undefined,
  dependencies: CmmdRuntimeBootstrapDependencies,
): Promise<EnvironmentConnection> {
  if (!savedCloud.isCmmdCloudEnvironment(record)) {
    return dependencies.ensureSavedEnvironmentConnection(record, options);
  }

  const generation = dependencies.getGeneration(record.environmentId);
  const canCommit = () =>
    dependencies.registry.isCurrent({
      record,
      generation,
      registryExpectation: options?.registryExpectation ?? { kind: "existing-record", record },
    });
  let lastError: unknown = null;
  for (
    let attemptIndex = 0;
    attemptIndex < CMMD_RUNTIME_BOOTSTRAP_MAX_ATTEMPTS;
    attemptIndex += 1
  ) {
    throwIfAborted(options?.signal);
    try {
      const connection = await dependencies.ensureSavedEnvironmentConnection(record, options);
      await raceWithAbort(connection.ensureBootstrapped(), options?.signal);
      return connection;
    } catch (error) {
      lastError = error;
      await handleBootstrapFailure({
        error,
        attemptIndex,
        record,
        options,
        dependencies,
        canCommit,
      });
    }
  }

  const finalError = lastError instanceof Error ? lastError : new Error(String(lastError));
  if (canCommit()) {
    dependencies.setRuntimeError(record.environmentId, finalError);
  }
  throw finalError;
}

async function handleBootstrapFailure(input: {
  readonly error: unknown;
  readonly attemptIndex: number;
  readonly record: SavedEnvironmentRecord;
  readonly options: CmmdRuntimeBootstrapOptions | undefined;
  readonly dependencies: CmmdRuntimeBootstrapDependencies;
  readonly canCommit: () => boolean;
}): Promise<void> {
  const { error, attemptIndex, record, options, dependencies, canCommit } = input;
  if (!canCommit()) throw error;
  await dependencies.removeConnection(record.environmentId).catch(() => false);
  throwIfAborted(options?.signal);
  if (
    attemptIndex >= CMMD_RUNTIME_BOOTSTRAP_MAX_ATTEMPTS - 1 ||
    !isTransientCmmdRuntimeBootstrapError(error)
  ) {
    if (canCommit()) dependencies.setRuntimeError(record.environmentId, error);
    throw error;
  }
  const retryDelayMs = cmmdRuntimeBootstrapRetryDelayMs(attemptIndex);
  options?.onBootstrapRetry?.({
    attempt: attemptIndex + 2,
    maxAttempts: CMMD_RUNTIME_BOOTSTRAP_MAX_ATTEMPTS,
    retryDelayMs,
    error,
  });
  if (canCommit()) {
    useSavedEnvironmentRuntimeStore.getState().patch(record.environmentId, {
      connectionState: "connecting",
      lastError: null,
      lastErrorAt: null,
    });
  }
  await sleepWithAbort(retryDelayMs, options?.signal);
}
